import * as pdfjsLib from 'pdfjs-dist';
import { getApiUrl } from '../config/api';
import { getPresignedPdfUrl } from './pdfPresignedUrl';

type PdfLoadOptions = {
  disableRange?: boolean;
};

async function loadPdfFromUrl(
  url: string,
  options: PdfLoadOptions & { httpHeaders?: Record<string, string> } = {},
): Promise<pdfjsLib.PDFDocumentProxy> {
  const task = pdfjsLib.getDocument({
    url,
    withCredentials: false,
    disableRange: options.disableRange ?? false,
    disableStream: options.disableRange ?? false,
    httpHeaders: options.httpHeaders,
  });
  return task.promise;
}

async function loadPdfFromPresignedUrl(
  presignedUrl: string,
): Promise<pdfjsLib.PDFDocumentProxy> {
  try {
    return await loadPdfFromUrl(presignedUrl);
  } catch (rangeError) {
    console.warn('Presigned PDF range load failed, retrying without range requests', rangeError);
    return loadPdfFromUrl(presignedUrl, { disableRange: true });
  }
}

async function loadPdfFromApiProxy(
  bucketKey: string,
  token: string,
): Promise<pdfjsLib.PDFDocumentProxy> {
  const apiUrl = `${getApiUrl('/stream-pdf')}?bucket_key=${encodeURIComponent(bucketKey)}`;
  const response = await fetch(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load PDF from API (${response.status})`);
  }

  const data = await response.arrayBuffer();
  return pdfjsLib.getDocument({ data }).promise;
}

export async function loadPdfDocument(
  bucketKey: string,
  token: string,
  forceRefresh = false,
): Promise<pdfjsLib.PDFDocumentProxy> {
  // CFN deploys use an external PDF corpus bucket without stack-managed S3 CORS.
  // Default to same-origin /api/stream-pdf; set VITE_PDF_PREFER_PRESIGNED_URL=true
  // after corpus bucket CORS is configured (see devops/aws/README-pdf-corpus-s3-cors.md).
  const preferPresignedUrl = import.meta.env.VITE_PDF_PREFER_PRESIGNED_URL === 'true';

  if (preferPresignedUrl) {
    const presignedUrl = await getPresignedPdfUrl(bucketKey, token, forceRefresh);
    try {
      return await loadPdfFromPresignedUrl(presignedUrl);
    } catch (directError) {
      console.warn(
        'Direct S3 PDF load failed; falling back to API proxy',
        directError,
      );
      return loadPdfFromApiProxy(bucketKey, token);
    }
  }

  try {
    return await loadPdfFromApiProxy(bucketKey, token);
  } catch (proxyError) {
    console.warn('API PDF proxy failed; trying presigned S3 URL', proxyError);
    const presignedUrl = await getPresignedPdfUrl(bucketKey, token, forceRefresh);
    return loadPdfFromPresignedUrl(presignedUrl);
  }
}
